import React, { useState, useRef, useEffect } from 'react';
import { Loader2, Send, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import MacroPicker from '@/components/admin/crm/MacroPicker';
import { insertMacroInto, restoreCaret } from '@/lib/crmMacros';

// Frontend mirror of base44/shared/sms.ts normalizeUKPhone.
function normalizePhone(raw) {
  if (!raw) return null;
  let s = String(raw).trim().replace(/[\s\-().]/g, '');
  if (!s) return null;
  if (s.startsWith('+44')) s = '+44' + s.slice(3);
  else if (s.startsWith('44') && s.length >= 11) s = '+' + s;
  else if (s.startsWith('0')) s = '+44' + s.slice(1);
  else if (/^7\d{9}$/.test(s)) s = '+44' + s;
  else return null;
  if (!/^\+44\d{9,10}$/.test(s)) return null;
  return s;
}

const MAX_CHARS = 918;

export default function SmsConversationTimeline({ phone, customerName, compact }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const replyRef = useRef(null);

  const insertMacro = (text) => {
    const { next, caret } = insertMacroInto(replyRef.current, text);
    setReply(next.slice(0, MAX_CHARS));
    restoreCaret(replyRef.current, caret);
  };

  const normalised = normalizePhone(phone);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['sms-thread', normalised],
    queryFn: () => base44.entities.SmsMessage.filter({ phone_number: normalised }, 'created_date', 200),
    enabled: !!normalised,
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || !normalised) return;
    setSending(true);
    try {
      const res = await base44.functions.invoke('sendBookingSms', {
        to: normalised,
        message: reply.trim(),
      });
      if (res.data?.success) {
        toast({ title: 'SMS sent', description: `Delivered to ${normalised}` });
        setReply('');
        queryClient.invalidateQueries({ queryKey: ['sms-thread', normalised] });
      } else {
        throw new Error(res.data?.error || 'Send failed');
      }
    } catch (e) {
      toast({ title: 'Could not send SMS', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (!normalised) {
    return (
      <div className="flex items-center gap-3 text-white/30 text-xs py-6 border border-white/5 bg-[#0d0d0f] px-5">
        No valid phone number on file for this customer.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex items-center gap-3 text-white/30 text-xs py-6 border border-white/5 bg-[#0d0d0f] px-5">
        No SMS conversation history for this number.
      </div>
    );
  }

  return (
    <div className="border border-white/5 bg-[#0d0d0f] flex flex-col" style={{ minHeight: compact ? '300px' : '400px' }}>
      {/* Thread */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto p-4 space-y-3 ${compact ? 'max-h-[300px]' : 'max-h-[450px]'}`}>
        {messages.map((msg) => {
          const isInbound = msg.direction === 'inbound';
          return (
            <div key={msg.id} className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[75%] ${isInbound ? 'items-start' : 'items-end'}`}>
                <div className={`flex items-center gap-1.5 mb-1 ${isInbound ? 'justify-start' : 'justify-end'}`}>
                  {isInbound ? (
                    <ArrowDownLeft className="w-3 h-3 text-green-400/60" />
                  ) : (
                    <ArrowUpRight className="w-3 h-3 text-primary/60" />
                  )}
                  <span className="text-white/30 text-[9px] font-bold tracking-widest">
                    {isInbound ? 'INBOUND' : 'OUTBOUND'}
                  </span>
                  {msg.source === 'reminder_sms' && (
                    <span className="text-white/20 text-[8px] tracking-widest">· AUTO</span>
                  )}
                </div>
                <div
                  className={`px-3 py-2 text-sm break-words ${
                    isInbound
                      ? 'bg-white/5 border border-white/10 text-white/90 rounded-r-lg rounded-tl-lg'
                      : 'bg-primary/15 border border-primary/30 text-white/90 rounded-l-lg rounded-tr-lg'
                  }`}
                >
                  {msg.body}
                </div>
                <p className={`text-white/20 text-[9px] mt-1 ${isInbound ? 'text-left' : 'text-right'}`}>
                  {msg.created_date ? format(new Date(msg.created_date), 'dd MMM yyyy · HH:mm') : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply composer */}
      <div className="border-t border-white/5 p-3 space-y-2">
        <MacroPicker
          channel="sms"
          context={{ customer_name: customerName }}
          onInsert={insertMacro}
        />
        <textarea
          ref={replyRef}
          value={reply}
          onChange={(e) => setReply(e.target.value.slice(0, MAX_CHARS))}
          rows={2}
          placeholder={`Reply to ${customerName || normalised}...`}
          className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2 resize-none focus:outline-none focus:border-primary/60 placeholder:text-white/20"
        />
        <div className="flex items-center justify-between">
          <span className="text-white/20 text-[9px]">{reply.length} / {MAX_CHARS}</span>
          <button
            onClick={handleSend}
            disabled={sending || !reply.trim()}
            className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'SENDING…' : 'SEND SMS'}
          </button>
        </div>
      </div>
    </div>
  );
}